/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useFetchVersionsListQuery } from '@services/versionHistoryService';
// import { getAllRevisions } from '../../../../api/revisions'
import { useApplicationState } from '../../../../hooks/common/use-application-state';
import { AsyncLoadingBoundary } from '../../../common/async-loading-boundary/async-loading-boundary';
import { RevisionListEntry } from './revision-list-entry';
import React, { useCallback, useMemo } from 'react';
import { ListGroup } from 'react-bootstrap';
// import { useAsync } from 'react-use';
// import { useSingleStringUrlParameter } from '@hooks/common/use-single-string-url-parameter';
import { VersionMetadata } from '@api/revisions/types';
import { VersionIndexType } from './revision-modal';
import { getFormattedTime } from './utils';

export interface RevisionListProps {
  selectedVersionIndex: VersionIndexType | undefined;
  onVersionSelect: Function;
  versionContentTrigger: Function;
}

/**
 * The list of selectable revisions of the current note.
 *
 * @param selectedVersionIndex The currently selected revision
 * @param onVersionSelect Callback that is executed when a list entry is selected
 */
export const RevisionList: React.FC<RevisionListProps> = ({
  selectedVersionIndex,
  onVersionSelect,
  versionContentTrigger
}) => {
  const entitySlug = useApplicationState(
    (state) => state.noteDetails.aliases[0].noteId
  );
  const {
    currentData: versionsListData,
    isFetching: versionsListIsFetching,
    error: versionsListError
  } = useFetchVersionsListQuery({
    entitySlug
  });

  const handleNewVerSelect = useCallback(
    (index: number) => {
      const oldVerIndex: number = (index + 1) % versionsListData?.versionsCount;
      const allVer = versionsListData?.versions;
      const versions: string[] = [
        allVer[index].attributes.version_id,
        allVer[oldVerIndex].attributes.version_id
      ];
      versionContentTrigger(
        {
          entitySlug,
          versionID: versions //[oldVer, newVer]
        },
        true
      );

      return onVersionSelect({
        newVerIndex: index,
        oldVerIndex,
        verDetails: {
          old: {
            date: `${getFormattedTime(
              allVer[oldVerIndex].attributes.created_at,
              'DD TT'
            )} ${oldVerIndex === 0 ? '(Current Version)' : ''}`,
            userData: allVer[oldVerIndex].attributes.user_data
          },
          new: {
            date: `${getFormattedTime(
              allVer[index].attributes.created_at,
              'DD TT'
            )} ${index === 0 ? '(Current Version)' : ''}`,
            userData: allVer[index].attributes.user_data
          }
        }
      });
    },
    [entitySlug, onVersionSelect, versionContentTrigger, versionsListData]
  );

  const revisionList = useMemo(() => {
    return versionsListData?.versions.map(
      (revisionListEntry: VersionMetadata, index: number) => (
        <RevisionListEntry
          isCurrentVersion={index === 0}
          active={selectedVersionIndex !== undefined ? selectedVersionIndex.newVerIndex === index : false}
          onSelect={() => handleNewVerSelect(index)}
          revision={revisionListEntry}
          key={revisionListEntry.id}
        />
      )
    );
  }, [handleNewVerSelect, selectedVersionIndex, versionsListData]);

  const errorToProp = useMemo(() => {
    if (versionsListError) {
      if ('message' in versionsListError) {
        return new Error(versionsListError.message);
      } else if ('status' in versionsListError) {
        return new Error(
          `HTTP error with status code ${versionsListError.status}`
        );
      }
    }
    return undefined;
  }, [versionsListError]);

  return (
    <AsyncLoadingBoundary
      loading={versionsListIsFetching}
      error={errorToProp}
      componentName={'revision list'}>
      <ListGroup>{revisionList}</ListGroup>
    </AsyncLoadingBoundary>
  );
};
